import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { WizardProse, WizardButtons } from '@/components/WizardParts'
import { GridTable } from '@/components/GridTable'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'
import type { WizardStage, PrincipalMap } from '@/layers/restore-wizard/types'

type RestoreInput = components['schemas']['RestoreInput']
type PrincipalMapProposal = components['schemas']['PrincipalMapProposal']
type PlanStage = Extract<WizardStage, { stage: 'plan' }>
type RemapEntryState = { readonly restoreInput: RestoreInput; readonly returnPath: string; readonly plan: PlanStage } | undefined

/**
 * `v-remap` — process/09-SCREEN-SPECS/v-remap.md. **The proposal is served, never computed
 * client-side from two member lists** (rule 2) — `restoreProposePrincipalMap` takes the same
 * `RestoreInput` `restoreCreatePlan` does, so this screen needs one already assembled (a
 * cross-workspace restore's `destination_workspace_id` set), read from `location.state`.
 *
 * **`RestoreWizardLayer`'s own destination-workspace picker is this screen's first real caller
 * (W155, closing the W146 gap this screen's own comment used to report).** Reached by navigating
 * OUT of the wizard's own reducer-driven flow — `remap` is registered as its own route "under
 * Restore" (`design/27-ui-surface-and-navigation.md`), not embedded in any one wizard family, so a
 * route round trip is the shape rather than an in-wizard pane. "Continue" hands `principal_map`
 * back to `returnPath` as `{ resumeAtPlan: <the wizard's own 'plan' stage> }`, which
 * `RestoreWizardLayer`'s own `useEffect` resumes directly.
 *
 * **"Drop all unmatched" is a local decision, not a second server write** — `restoreProposePrincipalMap`
 * stores nothing (its own description says so), so accepting drops only shapes what this screen
 * hands back to the wizard's own `RestoreInput.principal_map`. The spec's own Actions table maps
 * the button to the same operation that already ran to fetch the proposal; built here as a
 * local-only toggle, since firing the identical read again would change nothing.
 *
 * **`wizard-prose` added W166** — the intro sentence explaining what "dropped" means, previously a
 * `Note`, now the shared `WizardProse` — the same explanatory-intro role it already played.
 *
 * **Aggregate drop summary added** — once "Drop all unmatched" is confirmed, a line naming how
 * many principals were dropped and how many rules that touches, summed from each unmatched row's
 * own `used_by` label (e.g. `"4 rules"`) rather than restated as a bare count — a drop affecting
 * one rule and one affecting forty are different acts, and the per-row label is the only place
 * that distinction already lives (`used_by`'s own description).
 */
export function VRemap() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const location = useLocation()
  const navigate = useNavigate()
  const entry = location.state as RemapEntryState
  const restoreInput = entry?.restoreInput
  const [droppedAll, setDroppedAll] = useState(false)

  const proposal = useMutation({
    mutationFn: async (): Promise<PrincipalMapProposal> => {
      const client = getApiClient()
      const { data, error } = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/restore-principal-maps', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        body: restoreInput!,
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as PrincipalMapProposal
    },
  })

  useEffect(() => {
    if (restoreInput) proposal.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreInput])

  if (!restoreInput) {
    return (
      <div data-scr={unitId('v-remap')}>
        <h1 className="m-0 mb-6 text-section font-semibold">Who these rules apply to at the destination</h1>
        <Note>No cross-workspace restore is in progress — this screen is reached from a restore's own destination step, and none led here.</Note>
      </div>
    )
  }

  const problem = proposal.error instanceof ApiProblemError ? proposal.error.problem : undefined
  const droppedCount = droppedAll ? (proposal.data?.unmatched.length ?? 0) : 0
  const droppedRuleCount = droppedAll
    ? (proposal.data?.unmatched.reduce((sum, u) => sum + Number(/^\d+/.exec(u.used_by ?? '')?.[0] ?? 0), 0) ?? 0)
    : 0

  return (
    <div data-scr={unitId('v-remap')}>
      <h1 className="m-0 mb-6 text-section font-semibold">Who these rules apply to at the destination</h1>
      <WizardProse>A principal that does not exist at the destination is dropped, and it is named — in the report and in the audit record.</WizardProse>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      {proposal.data && (
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mt-6">
          <div data-cmp="panel-header" className="flex items-center gap-5 p-6 border-b border-bd">
            <h3 className="m-0 text-body-lg font-semibold flex-1">Matched</h3>
          </div>
          <GridTable
            columns={[
              { key: 'source', header: 'Source', render: (m) => m.source_principal },
              { key: 'kind', header: 'Kind', render: (m) => m.kind },
              { key: 'usedBy', header: 'Used by', render: (m) => <span className="text-muted">{m.used_by ?? '—'}</span> },
              { key: 'destination', header: 'Destination', render: (m) => m.destination_principal },
            ]}
            rows={proposal.data.matched}
            getRowKey={(m) => `${m.kind}:${m.source_principal}`}
          />

          <div data-cmp="panel-header" className="flex items-center gap-5 p-6 border-b border-t border-bd">
            <h3 className="m-0 text-body-lg font-semibold flex-1">Unmatched</h3>
            <div data-cmp="grid-tools">
              <Button data-act={actionId('restore.proposePrincipalMap')} ghost disabled={droppedAll} onClick={() => setDroppedAll(true)}>
                Drop all unmatched
              </Button>
            </div>
          </div>
          <GridTable
            columns={[
              { key: 'principal', header: 'Principal', render: (u) => u.display ?? u.source_principal },
              { key: 'kind', header: 'Kind', render: (u) => u.kind },
              { key: 'usedBy', header: 'Used by', render: (u) => <span className="text-muted">{u.used_by ?? '—'}</span> },
              { key: 'decision', header: 'Decision', render: () => (droppedAll ? 'Dropped' : 'Pending a decision') },
            ]}
            rows={proposal.data.unmatched}
            getRowKey={(u) => `${u.kind}:${u.display ?? u.source_principal}`}
            emptyMessage="Every principal matched."
          />
          {droppedCount > 0 && (
            <div className="px-5 pt-4">
              <Note>
                {droppedCount} principal{droppedCount === 1 ? '' : 's'} dropped, affecting {droppedRuleCount} rule{droppedRuleCount === 1 ? '' : 's'}.
              </Note>
            </div>
          )}
          <div data-cmp="grid-footer" className="border-t border-bd p-5">
            <Note>Ranking two permission levels needs a named model, and repository permission ordering is sourced nowhere at all — no surface here ranks one.</Note>
          </div>
        </div>
      )}

      {proposal.data && entry && (
        <WizardButtons>
          <Button
            data-act={actionId('restore.proposePrincipalMap')}
            disabled={proposal.data.unmatched.length > 0 && !droppedAll}
            onClick={() => {
              const principalMap: PrincipalMap = [
                ...proposal.data!.matched.map((m) => ({ source_principal: m.source_principal, action: 'MAP' as const, destination_principal: m.destination_principal })),
                ...(droppedAll ? proposal.data!.unmatched.map((u) => ({ source_principal: u.source_principal, action: 'DROP' as const })) : []),
              ]
              navigate(entry.returnPath, { state: { resumeAtPlan: { ...entry.plan, principalMap } } })
            }}
          >
            Continue
          </Button>
        </WizardButtons>
      )}
    </div>
  )
}
